/********************************************************* {COPYRIGHT-TOP} ***
 * Licensed Materials - Property of IBM
 *
 * (C) Copyright IBM Corporation 2016, 2024
 *
 * All Rights Reserved.
 * US Government Users Restricted Rights - Use, duplication or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 ********************************************************** {COPYRIGHT-END} **/

import React, { useEffect, useState } from 'react';
import { Loading, Tabs, Tab, Button, TabList, TabPanels, TabPanel } from '@carbon/react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Copier from './Copier';
import { cleanUpKey } from '../utils/common';
import scrollIntoView from 'scroll-into-view';
import ReactDOM from 'react-dom';
import { CheckmarkFilled, InformationFilled, Misuse, WarningFilled } from '@carbon/react/icons';
import { CodeViewer } from './CodeViewer';
import { View, ViewOff } from '@carbon/react/icons';
import { get, cloneDeep } from 'lodash';
import { useTranslation } from 'react-i18next';

const TestToolResponse = ({ invokeResponse, responseBody, contentFormat, mtlsEnabled, isInvoking, security }) => {
  const [showHidden, setShowHidden] = useState(false);
  const [hasConfidential, setHasConfidential] = useState(false);
  const { t } = useTranslation();

  const ensureVisible = () => {
    if (invokeResponse && isInvoking) {
      scrollIntoView(ReactDOM.findDOMNode(this));
    }
  };

  useEffect(() => {
    ensureVisible();
  });
  let filteredUrl = invokeResponse?.request?.url;

  if (!showHidden) {
    let headersToHide = ['x-ibm-client-secret', 'secret', 'password'];
    let queriesToHide = ['password', 'client_secret'];
    if (security?.flags?.clientSecretName && security.flags.clientSecretName !== '') {
      if (security?.flags?.clientSecretLocation === 'query') {
        queriesToHide.push(security.flags.clientSecretName);
      } else if (security?.flags?.clientSecretLocation === 'header') {
        headersToHide.push(security.flags.clientSecretName);
      }
    }
    invokeResponse = cloneDeep(invokeResponse);

    let keysToChange = [];
    if (get(invokeResponse, `request.headers`)) {
      let headerKeys = Object.keys(invokeResponse.request.headers);

      headersToHide.forEach((header) => {
        const filteredArr = headerKeys.filter((item) => item.toLowerCase().includes(header?.toLowerCase()));

        keysToChange = [...keysToChange, ...filteredArr];
      });
      keysToChange = new Set(keysToChange);
    }

    keysToChange.forEach((header) => {
      if (get(invokeResponse, `request.headers[${header}]`)) {
        invokeResponse.request.headers[header] = '*********';
        //Matches from <header>: to \n
        invokeResponse.request.headersAsString.replace(new RegExp(`${header}: (?:(?!\\n).)*`), `${header}: =************`);
        if (!hasConfidential) {
          setHasConfidential(true);
        }
      }
    });

    if (get(invokeResponse, `request.headers.Authorization`)) {
      let original = invokeResponse.request.headers.Authorization;
      invokeResponse.request.headers.Authorization = invokeResponse.request.headers.Authorization.replace(/Bearer [^\s]*/, 'Bearer ************');
      invokeResponse.request.headersAsString.replace(/Bearer [^\s]*/, 'Bearer ************');
      if (original !== invokeResponse.request.headers.Authorization && !hasConfidential) {
        setHasConfidential(true);
      }
    }
    if (get(invokeResponse, 'request.url')) {
      let original = invokeResponse.request.url;
      queriesToHide.forEach((query) => {
        filteredUrl = invokeResponse.request.url.replace(new RegExp(`${query}=[^&]*`), `${query}=************`);
      });
      if (original !== filteredUrl && !hasConfidential) {
        setHasConfidential(true);
      }
    }
  }

  let corsMsg = mtlsEnabled ? t('explorer_cors_mtls') : t('explorer_cors');

  return (
    <div className="testToolResult" data-testid="testtool-result">
      <div className="testToolSection testToolRequest">
        <div role="heading" aria-level={3} className="labelText">
          {t('request')}
        </div>
        <div data-testid="testRequestBody" className={classNames('testRequestBody', 'cds--snippet', 'cds--snippet--multi')}>
          {typeof invokeResponse.statusCode === 'undefined' && <Loading data-testid="loading-icon" className="invoking" small withOverlay={false} />}
          {typeof invokeResponse.statusCode !== 'undefined' && (
            <div className={classNames('body', 'cds--snippet-container')}>
              <div>
                <pre>
                  <code className={classNames('boundedSection testRequestContent')}>
                    <div className="requestDetails requestUrl">
                      {invokeResponse.request.method.toUpperCase()} {filteredUrl}
                    </div>
                    {invokeResponse.request.headers && (
                      <div>
                        <div className="requestDetails requestHeaders">{t('headers')}:</div>
                        <div className="requestDetails requestHeaders">
                          {Object.keys(invokeResponse.request.headers).map((headerName) => {
                            return (
                              <div key={headerName}>
                                {headerName}: {invokeResponse.request.headers[headerName]}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </code>
                </pre>
              </div>
            </div>
          )}
          {hasConfidential && (
            <Button
              hasIconOnly
              className="toggleHidden"
              data-testid="toggleHidden"
              kind="ghost"
              iconDescription={!showHidden ? 'Hide access token' : 'Show access token'}
              renderIcon={!showHidden ? View : ViewOff}
              onClick={() => {
                setShowHidden(!showHidden);
              }}
            />
          )}
          <Copier element=".testRequestContent" />
        </div>
      </div>
      <div className="testToolSection testToolResponse">
        <div role="heading" aria-level={3} className="labelText">
          {t('response')}
        </div>
        <div className={classNames('testResponseBody')}>
          {typeof invokeResponse.statusCode === 'undefined' && <Loading className="invoking" small withOverlay={false} />}
          {typeof invokeResponse.statusCode !== 'undefined' && (
            <div>
              <div className={classNames('responseDetails', 'responseStatus', `responseCode-${cleanUpKey(invokeResponse.statusCode + '')}`)}>
                {invokeResponse.statusCode >= 100 && invokeResponse.statusCode <= 199 && (
                  <InformationFilled size={16} aria-label="Informational" className={`cds--icon-information statusIcon`} name="information" role="img" />
                )}
                {invokeResponse.statusCode >= 200 && invokeResponse.statusCode <= 299 && (
                  <CheckmarkFilled size={16} aria-label="Success" className={`cds--icon-checkmark statusIcon`} name="checkmark" role="img" />
                )}
                {invokeResponse.statusCode >= 300 && invokeResponse.statusCode <= 399 && (
                  <WarningFilled size={16} aria-label="Redirection" className={`cds--icon-warning statusIcon`} name="warning" role="img" />
                )}
                {invokeResponse.statusCode >= 400 && <Misuse size={16} aria-label="Error" className={`cds--icon-misuse statusIcon`} name="error" role="img" />}
                <span className="statusCode">{` ${invokeResponse.statusCode}`}</span> {` ${invokeResponse.statusText}`}
                {invokeResponse.statusInfo && <div className="boxout">{invokeResponse.statusInfo}</div>}
              </div>
              <div>
                <div className={classNames('body', 'cds--snippet-container')}>
                  <div>
                    {invokeResponse.statusCode === 0 && (
                      <div className="contrast responseDetails responseBody">
                        <div className="explorerCorsError">{corsMsg}</div>
                        <div className="explorerCertificateError">{t('explorer_check_certificate')}</div>
                        <a className="responseUrlLink" target="_blank" rel="noopener noreferrer" href={invokeResponse.request.url}>
                          {filteredUrl}
                        </a>
                      </div>
                    )}
                    {invokeResponse.statusCode * 1 !== 0 && (
                      <Tabs>
                        <TabList aria-label="response tabs">
                          {[
                            invokeResponse.responseDataString && !invokeResponse.isFileDownload && (
                              <Tab id="response_bodyTab" key="bodyTab" className="bodyTab" title={t('body')}>
                                {t('body')}{' '}
                              </Tab>
                            ),
                            invokeResponse.headersAsString && (
                              <Tab id="response_headerTab" key="HeaderTab" className="HeaderTab" title={t('headers')}>
                                {t('headers')}
                              </Tab>
                            )
                          ].filter((tab) => tab)}
                        </TabList>
                        <TabPanels>
                          {[
                            invokeResponse.responseDataString && !invokeResponse.isFileDownload && (
                              <TabPanel>
                                <div className={classNames('cds--snippet', 'cds--snippet--multi', 'cds--snippet--expand')}>
                                  <pre className="contrast responseDetails responseBody">
                                    {!responseBody && <div className="spinner"></div>}

                                    {responseBody && (
                                      <CodeViewer data-testid={'operation-example-generated'} codeLanguage={contentFormat}>
                                        {responseBody}
                                      </CodeViewer>
                                    )}
                                  </pre>
                                  <Copier element=".responseBody" />
                                </div>
                              </TabPanel>
                            ),
                            invokeResponse.headersAsString && (
                              <TabPanel>
                                <div className={classNames('cds--snippet', 'cds--snippet--multi', 'cds--snippet--expand')}>
                                  <pre data-testid="response-headers" className="responseDetails responseHeaders">
                                    <code>
                                      {Object.keys(invokeResponse.headers).map((headerName) => {
                                        return (
                                          <div key={headerName}>
                                            {headerName}: {invokeResponse.headers[headerName]}
                                          </div>
                                        );
                                      })}
                                    </code>
                                  </pre>
                                  <Copier element=".responseHeaders" />
                                </div>
                              </TabPanel>
                            )
                          ].filter((tab) => tab)}
                        </TabPanels>
                      </Tabs>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

TestToolResponse.propTypes = {
  invokeResponse: PropTypes.object,
  isInvoking: PropTypes.bool,
  responseBody: PropTypes.string,
  mtlsEnabled: PropTypes.bool
};

export default TestToolResponse;
